import { Link } from 'react-router-dom';

export function FactionsPage() {
  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8">
        <Link
          to="/ignus"
          className="text-text-muted hover:text-accent-gold transition-colors inline-flex items-center gap-1"
        >
          <BackIcon className="w-4 h-4" />
          Back to Ignus
        </Link>
      </div>

      <h1 className="font-display text-3xl font-bold text-text-primary mb-6">
        The Four Factions of Ignus
      </h1>

      <div className="space-y-6">
        {/* Introduction */}
        <div className="card p-6">
          <p className="text-text-secondary">
            Ignus society is pretty rigidly structured. Everyone knows their place and, if they
            don't, they're often told what their place is. There are four major factions on Ignus.
            Each playable character class falls into one of these factions.
          </p>
        </div>

        {/* The Circle */}
        <div id="circle" className="card p-6 border-green-500/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <LeafIcon className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h2 className="font-display text-xl font-semibold text-green-400">The Circle</h2>
              <p className="text-sm text-text-muted">Druid, Barbarian, Ranger</p>
            </div>
          </div>

          <div className="space-y-4 text-text-secondary">
            <p>
              The Circle is the societal faction headed up by the druids. They revere Ignus "The
              Spark" as a sort of proto-titan (the PRIME titan, in fact) who is asleep. They believe
              that all forms of life (including the titans) are creations of magical energy bled
              from Ignus in its slumber. That all are, in effect, products of Ignus's dreams.
            </p>
            <p>
              They see themselves as shepherds and protectors of Ignus's magical flow. Their version
              of the apocalypse is Ignus waking up. To keep it sleeping, they must preserve magical
              and natural balance because this creates the comforting conditions that will keep
              Ignus asleep.
            </p>

            <div className="bg-background-elevated rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-text-primary">Leadership: The Stump</h4>
              <p className="text-sm">
                The Druids are organized into circles and governed by a ruling council made up of
                representatives from all of the circles. They are led by a triumvirate of elders
                which are literally the oldest druids in the council. This triumvirate is known as{' '}
                <span className="text-green-400">The Stump</span> and each member of The Stump is
                theoretically equal in status. The fourth oldest druid (essentially a member of The
                Stump in waiting) is{' '}
                <span className="text-green-400">Speaker for the Stump</span> and delivers all of
                The Stump's decrees, opinions, and questions.
              </p>
            </div>

            <div className="grid gap-3">
              <ClassCard
                name="Druid"
                classId="druid"
                description="The ruling class of the Circles. They lead, advise, and heal. They are protectors and guardians, sages and doctors. And sometimes, they're the embodiment of the savagery of the natural world."
                color="text-green-400"
              />
              <ClassCard
                name="Barbarian"
                classId="barbarian"
                description="The martial arm of the circles. Where the Druids are the voice of the land and the shepherds of their people, the Barbarians are the dogs of war. They work hand in hand with the druid circles to provide protection and enforce the will of The Stump. A Barbarian in full rage is a nightmare that few care to experience."
                color="text-green-400"
              />
              <ClassCard
                name="Ranger"
                classId="ranger"
                description="The forward scouts, the hunters, the guides, and the support troops. They are woodsmen and they tend to be a pretty pragmatic and down to earth group of people."
                color="text-green-400"
              />
            </div>
          </div>
        </div>

        {/* The Seat */}
        <div id="seat" className="card p-6 border-accent-gold/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-accent-gold/10 flex items-center justify-center">
              <CrownIcon className="w-5 h-5 text-accent-gold" />
            </div>
            <div>
              <h2 className="font-display text-xl font-semibold text-accent-gold">The Seat</h2>
              <p className="text-sm text-text-muted">Cleric, Paladin, Monk</p>
            </div>
          </div>

          <div className="space-y-4 text-text-secondary">
            <p>
              Made up of the devout faithful who are allied with and serve the pantheon of titans
              known as The Crystal Assembly, The Seat is a network of churches, temples, and other
              communities of devotion.
            </p>

            <div className="bg-background-elevated rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-text-primary">Leadership Structure</h4>
              <p className="text-sm">
                The clerics are organized more like the Vatican. There is a supreme cleric called{' '}
                <span className="text-accent-gold">The High Seat</span> and a{' '}
                <span className="text-accent-gold">Council of Primarchs</span> (referred to as
                brothers and sisters) from each of the main faiths. The paladins are led by a
                council of Zealots and their leader is called{' '}
                <span className="text-accent-gold">The Silver Fist of Heaven</span> (or "The Fist"
                for short).
              </p>
            </div>

            <div className="grid gap-3">
              <ClassCard
                name="Cleric"
                classId="cleric"
                description="Organized like the Vatican with a supreme cleric and a council of primarchs from each of the main faiths. They channel the divine power of the Titans to heal, protect, and guide the faithful."
                color="text-accent-gold"
              />
              <ClassCard
                name="Paladin"
                classId="paladin"
                description="The military might of the clerics. They enforce the will of The Seat. Led by a council of Zealots under The Silver Fist of Heaven, they are the holy warriors who stand against the enemies of the faithful."
                color="text-accent-gold"
              />
              <ClassCard
                name="Monk"
                classId="monk"
                description="Generally non-magic folks who still wish to pursue a holy lifestyle focus. Much like the monks from classic kung-fu movies, they live in cloistered communities but also have a sense of civic duty."
                color="text-accent-gold"
              />
            </div>
          </div>
        </div>

        {/* Civil Society */}
        <div id="civil" className="card p-6 border-blue-400/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-400/10 flex items-center justify-center">
              <ScaleIcon className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="font-display text-xl font-semibold text-blue-400">Civil Society</h2>
              <p className="text-sm text-text-muted">Bard, Fighter, Rogue</p>
            </div>
          </div>

          <div className="space-y-4 text-text-secondary">
            <p>
              Civil Society encompasses the everyday governance, protection, and less... savory
              operations that keep Ignus running. While The Circle and The Seat handle spiritual and
              magical matters, Civil Society deals with the practical business of civilization.
            </p>

            <div className="grid gap-3">
              <ClassCard
                name="Bard"
                classId="bard"
                description="Actually more like politicians and civil servants than in standard D&D. They often hold positions of power and influence. To become a bard, a person must go to one of the bard colleges where (supposedly) they receive rigorous conditioning and take several magically-binding oaths to only use their powers for the good of society."
                color="text-blue-400"
              />
              <ClassCard
                name="Fighter"
                classId="fighter"
                description="Civil soldiers and city guards, generally. Though you will find some mercenary bands doing odd jobs for folks. They are the backbone of civic defense and law enforcement."
                color="text-blue-400"
              />
              <ClassCard
                name="Rogue"
                classId="rogue"
                description="Obviously not beholden to any faction. But they do have guilds and can be contracted for work. They are thieves, spies, and assassins for hire. Their skills are often employed by those who need problems solved... discreetly."
                color="text-blue-400"
              />
            </div>
          </div>
        </div>

        {/* The Shunned */}
        <div id="shunned" className="card p-6 border-red-500/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
              <SkullIcon className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h2 className="font-display text-xl font-semibold text-red-400">The Shunned</h2>
              <p className="text-sm text-text-muted">Sorcerer, Warlock, Wizard</p>
            </div>
          </div>

          <div className="space-y-4 text-text-secondary">
            <p>
              On modern Ignus,{' '}
              <Link to="/ignus/magic#titanic" className="text-accent-gold hover:underline">
                Titanic (divine) magic
              </Link>{' '}
              and{' '}
              <Link to="/ignus/magic#nature" className="text-accent-gold hover:underline">
                nature magic
              </Link>{' '}
              are considered wholesome, good, and healthy while{' '}
              <Link to="/ignus/magic#arcane" className="text-red-400 font-medium hover:underline">
                arcane magic
              </Link>{' '}
              is considered by most to be disgusting, deviant, and dangerous.
            </p>

            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <WarningIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-300">
                  <strong>Player Warning:</strong> If you want to play a class on the Shunned list,
                  you will need to come up with a cover story for how they're existing in society
                  when they're pursuing forbidden arcane knowledge.
                </p>
              </div>
            </div>

            <div className="grid gap-3">
              <ClassCard
                name="Sorcerer"
                classId="sorcerer"
                description="Considered aberrations. People who are born with innate magical powers are encouraged to enter the clergy or join the druids ASAP to channel their power in 'healthy' ways (essentially multiclassing). Sorcerous powers are unclean and most folks who have innate abilities hide them as well as they can."
                color="text-red-400"
              />
              <ClassCard
                name="Warlock"
                classId="warlock"
                description="Treated like villains with corrupt souls who would do anything for power. They are characterized as having made profane pacts with unclean entities for evil gain. Warlocks seek power from somewhere besides the gods or Ignus itself."
                color="text-red-400"
              />
              <ClassCard
                name="Wizard"
                classId="wizard"
                description="Treated like mad scientists who don't care about anyone or anything and don't consider the moral implications of what they're doing. They refuse to kneel to the will of the gods and they don't care about Ignus. They seek selfish gain above all."
                color="text-red-400"
              />
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="card p-6 bg-background-elevated">
          <h2 className="font-display text-lg font-semibold text-text-primary mb-4">
            Continue Exploring
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link
              to="/ignus/classes"
              className="flex items-center gap-3 p-3 rounded-lg bg-background-card hover:bg-background-secondary transition-colors"
            >
              <ClassIcon className="w-5 h-5 text-accent-gold" />
              <span className="text-text-primary">Browse Classes</span>
            </Link>
            <Link
              to="/ignus/magic"
              className="flex items-center gap-3 p-3 rounded-lg bg-background-card hover:bg-background-secondary transition-colors"
            >
              <MagicIcon className="w-5 h-5 text-accent-gold" />
              <span className="text-text-primary">Magic & Titans</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClassCard({
  name,
  description,
  color,
  classId,
}: {
  name: string;
  description: string;
  color: string;
  classId: string;
}) {
  return (
    <div className="bg-background-elevated rounded-lg p-3">
      <Link to={`/ignus/classes#${classId}`} className={`font-medium ${color} mb-1 hover:underline`}>
        {name}
      </Link>
      <p className="text-sm text-text-secondary">{description}</p>
    </div>
  );
}

// Icons
function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function LeafIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function CrownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
    </svg>
  );
}

function ScaleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
    </svg>
  );
}

function SkullIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H9v-1a3 3 0 016 0v1zm0 0h3m-3 0h-3m3-5a4 4 0 00-6 0" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function ClassIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}

function MagicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}
